import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import bus from '@/core/bus';
import { BusConstants, StorageConstants } from '@/core/constants';
import { LocalStorageService } from '@/services/storage/storage.service';
import {
    Realm,
    Realmlist,
    UpdateRealmNameRequest,
    AddRealmRequest,
    EditRealmRequest,
    DeleteRealmRequest,
    UpdateRealmAllowedSecurityLevelRequest,
    UpdateRealmsViewModel
} from '@/types/apiServerContract';
import { RealmApi } from '@/services/api/api.realms';

@Module({ namespaced: true, dynamic: true, store, name: 'RealmsModule' })
class RealmsState extends VuexModule {
	realms: Realm[] = [];
	realmLists: Realmlist[] = [];
	activeRealmId: number = -1;
	interval: any = 0;

	get ActiveRealm(): Realmlist | null {
	    return this.activeRealmId < 1 ? null : this.realmLists.find(x => x.id === this.activeRealmId) ?? null;
	}

	@Mutation
	SetRealms(realms: Realm[]) {
	    for (const realm of realms) {
	        const existingRealm = this.realms.find(x => x.id === realm.id);
	        if (existingRealm) {
	            Object.assign(existingRealm, realm);
	        } else {
	            this.realms.push(realm);
	        }
	    }
	}

	@Mutation
	SetRealmLists(realms: Realmlist[]) {
	    for (const realm of realms) {
	        const existingRealm = this.realmLists.find(x => x.id === realm.id);
	        if (existingRealm) {
	            Object.assign(existingRealm, realm);
	        } else {
	            this.realmLists.push(realm);
	        }
	    }
	}

	@Mutation
	RemoveRealm(id: number) {
	    this.realms = [...this.realms].splice(
	        this.realms.findIndex(x => x.id === id),
	        1
	    );
	    this.realmLists = [...this.realmLists].splice(
	        this.realmLists.findIndex(x => x.id === id),
	        1
	    );
	}

	@Mutation
	SetInterval(interval: any) {
	    this.interval = interval;
	}

	@Mutation
	SetActiveRealm(realmId: number) {
	    this.activeRealmId = Object.freeze(realmId);

	    if (realmId > 0) {
	        LocalStorageService.setItem(StorageConstants.ActiveRealm, realmId);
	    } else {
	        LocalStorageService.removeItem(StorageConstants.ActiveRealm);
	    }

	    bus.emit(BusConstants.SelectedRealmChanged);
	}

	@Action({ rawError: true })
	async GetRealmLists() {
	    const result = await RealmApi.GetRealms();
	    this.SetRealmLists(result.realmList);
	}

	@Action({ rawError: true })
	async GetRealmInformations() {
	    const result = await RealmApi.GetRealmInformations();
	    this.SetRealms(result.realms);
	}

	@Action({ rawError: true })
	async AddRealm(request: AddRealmRequest) {
	    const response = await RealmApi.AddRealm(request);
	    if (response.realm && response.realmlist) {
	        this.SetRealms([...this.realms, response.realm]);
	        this.SetRealmLists([...this.realmLists, response.realmlist]);
	    }
	}

	@Action({ rawError: true })
	async EditRealm(request: EditRealmRequest): Promise<UpdateRealmsViewModel> {
	    const response = await RealmApi.EditRealm(request);
	    if (response.realm && response.realmlist) {
	        this.RemoveRealm(request.id);
	        this.SetRealms([...this.realms, response.realm]);
	        this.SetRealmLists([...this.realmLists, response.realmlist]);
	    }
	    return response;
	}

	@Action({ rawError: true })
	async DeleteRealm(request: DeleteRealmRequest): Promise<boolean> {
	    const result = await RealmApi.DeleteRealm(request);
	    if (result.data) this.RemoveRealm(request.id);
	    return result.data;
	}

	@Action({ rawError: true })
	async UpdateRealmName(request: UpdateRealmNameRequest): Promise<UpdateRealmsViewModel> {
	    const response = await RealmApi.UpdateRealmName(request);
	    if (response.realm && response.realmlist) {
	        this.RemoveRealm(request.realmId);
	        this.SetRealms([...this.realms, response.realm]);
	        this.SetRealmLists([...this.realmLists, response.realmlist]);
	    }
	    return response;
	}

	@Action({ rawError: true })
	async UpdateAllowedSecurityLevel(request: UpdateRealmAllowedSecurityLevelRequest): Promise<UpdateRealmsViewModel> {
	    const response = await RealmApi.UpdateAllowedSecurityLevel(request);
	    if (response.realm && response.realmlist) {
	        this.RemoveRealm(request.realmId);
	        this.SetRealms([...this.realms, response.realm]);
	        this.SetRealmLists([...this.realmLists, response.realmlist]);
	    }
	    return response;
	}

	@Mutation
	Reset() {
	    this.activeRealmId = -1;
	    clearInterval(this.interval);
	    this.interval = null;
	    LocalStorageService.removeItem(StorageConstants.ActiveRealm);
	}

	@Action({ rawError: true })
	Clear() {
	    this.Reset();
	}

	@Action({ rawError: true })
	async Init() {
	    await this.GetRealmInformations();
	    const realmId = LocalStorageService.getItemAs<number>(StorageConstants.ActiveRealm);
	    this.SetActiveRealm(realmId || -1);

	    const interval = setInterval(() => this.GetRealmInformations(), 60000);
	    this.SetInterval(interval);
	}
}

const RealmsModule = getModule(RealmsState);

export { RealmsModule };
